import { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import type { Ticket } from '@runninglime/shared';
import { apiGet, apiPost } from '../../lib/api.ts';
import { formatDate } from '../../lib/utils.ts';
import { useAuth } from '../../context/AuthContext.tsx';
import { StatusBadge, PriorityBadge, QueueBadge, LoadingSpinner } from '../../components/ui/Shared.tsx';

export default function TicketDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const prevMsgCount = useRef<number | null>(null);

  const load = async () => {
    const res = await apiGet<Ticket>(`/tickets/${id}`);
    if (res.success && res.data) setTicket(res.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    if (!ticket) return;
    const count = ticket.messages.length;
    // Only auto-scroll when a new message is added, not on initial load
    if (prevMsgCount.current !== null && count > prevMsgCount.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    prevMsgCount.current = count;
  }, [ticket?.messages.length]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;
    setSending(true);
    const res = await apiPost(`/tickets/${id}/messages`, { text: message.trim() });
    if (res.success) {
      setMessage('');
      await load();
    }
    setSending(false);
  };

  if (loading) return <LoadingSpinner />;
  if (!ticket) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8 text-center">
        <p className="text-gray-500">Ticket not found</p>
        <Link to="/support" className="text-green mt-2 inline-block hover:underline">Back to Support</Link>
      </div>
    );
  }

  const isClosed = ticket.status === 'closed';

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in">
      <Link to="/support" className="text-sm text-gray-500 hover:text-green transition mb-4 inline-flex items-center gap-1">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Tickets
      </Link>

      {/* Header */}
      <div className="glass rounded-2xl p-6 mb-6">
        <div className="flex items-start gap-3 flex-wrap mb-3">
          <StatusBadge status={ticket.status} />
          <PriorityBadge priority={ticket.priority} />
          <QueueBadge queue={ticket.queue} />
          {ticket.orderId && (
            <Link to={`/orders/${ticket.orderId}`} className="text-xs text-green hover:underline">
              Order #{ticket.orderId.slice(0, 8)}
            </Link>
          )}
        </div>
        <h1 className="text-2xl font-bold text-dark">{ticket.subject}</h1>
        <p className="text-sm text-gray-500 mt-1">Opened {formatDate(ticket.createdAt)}</p>
        {ticket.assignment && (
          <p className="text-xs text-gray-400 mt-1">Assigned to {ticket.assignment.assignedToName}</p>
        )}
      </div>

      {/* Messages */}
      <div className="space-y-4 mb-6 stagger-children">
        {ticket.messages.map((msg) => {
          const isMe = msg.from === 'customer';
          return (
            <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-2xl px-5 py-3 ${
                isMe
                  ? 'bg-gradient-to-br from-lime to-green text-dark rounded-br-md'
                  : 'bg-white shadow-sm rounded-bl-md'
              }`}>
                <div className={`text-xs mb-1 font-medium ${isMe ? 'text-dark/70' : 'text-gray-400'}`}>
                  {isMe ? 'You' : msg.senderName} · {formatDate(msg.timestamp)}
                </div>
                <p className={`text-sm leading-relaxed whitespace-pre-wrap ${isMe ? 'text-dark' : 'text-gray-700'}`}>
                  {msg.text}
                </p>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Reply Form */}
      {!isClosed ? (
        <form onSubmit={handleSend} className="glass rounded-2xl p-4">
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Type your reply..."
            rows={3}
            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-lime/50 focus:border-lime outline-none transition resize-none mb-3"
          />
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={sending || !message.trim()}
              className="btn-gradient px-6 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50 active:scale-[0.97] transition-transform"
            >
              {sending ? 'Sending...' : 'Send Reply'}
            </button>
          </div>
        </form>
      ) : (
        <div className="glass rounded-2xl p-6 text-center text-gray-500 text-sm">
          This ticket is closed. Open a new ticket if you need further help.
        </div>
      )}
    </div>
  );
}
